/**
 * Age calculation utility functions
 * Calculates current age dynamically from date of birth
 */

/**
 * Calculate current age from date of birth
 * @param dateOfBirth - Date string in format "YYYY-MM-DD"
 * @returns Current age in years
 */
export const calculateAge = (dateOfBirth: string | null | undefined): number => {
  if (!dateOfBirth) return 0;
  
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // If birthday hasn't occurred yet this year, subtract 1
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return Math.max(0, age); // Ensure age is never negative
  } catch (error) {
    console.error('Error calculating age:', error);
    return 0;
  }
};

/**
 * Format age for display
 * @param age - Age in years
 * @returns Formatted age string (e.g., "Less than a year", "3 years old")
 */
export const formatAge = (age: number): string => {
  if (age === 0) {
    return 'Less than a year';
  }
  return `${age} ${age === 1 ? 'year' : 'years'} old`;
};

/**
 * Get age from date of birth and format for display
 * @param dateOfBirth - Date string in format "YYYY-MM-DD"
 * @returns Formatted age string
 */
export const getFormattedAge = (dateOfBirth: string | null | undefined): string => {
  const age = calculateAge(dateOfBirth);
  return formatAge(age);
};

